import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Lock, Eye, EyeOff, LogOut, ArrowLeft, Calculator, ArrowUpRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const SS_KEY = 'px_estimate_data';

export default function EstimateCost() {
  const [params, setParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [systems, setSystems] = useState(null);

  useEffect(() => {
    const cached = sessionStorage.getItem(SS_KEY);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          setSystems(parsed);
        } else {
          sessionStorage.removeItem(SS_KEY);
        }
      } catch (e) {
        sessionStorage.removeItem(SS_KEY);
      }
    }
  }, []);

  const unlock = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/api/site/estimate-unlock`, { password });
      setSystems(data.systems);
      sessionStorage.setItem(SS_KEY, JSON.stringify(data.systems));
      setPassword('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Password salah');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(SS_KEY);
    setSystems(null);
  };

  const sKey = params.get('s');
  const estimate = systems
    ? systems[sKey] || Object.values(systems)[0]
    : null;
  const activeKey = systems
    ? systems[sKey]
      ? sKey
      : Object.keys(systems)[0]
    : null;

  return (
    <div data-testid="page-estimate" className="pt-[68px] md:pt-[100px] bg-white min-h-screen">
      {/* Hero */}
      <section className="relative bg-neutral-950 overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, rgba(239,68,68,0.35), transparent 50%)',
          }}
        />
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-12 py-16 md:py-24 text-white">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-white mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Kembali ke Beranda
          </Link>
          <p className="text-[11px] tracking-[0.3em] text-red-400 font-semibold mb-3">
            KHUSUS DEALER & SALES
          </p>
          <h1 className="font-display text-4xl md:text-5xl font-bold leading-tight max-w-3xl">
            Cek Estimasi Biaya Sistem
          </h1>
          <p className="text-neutral-300 mt-4 max-w-2xl">
            Rincian item produk, jumlah (qty), dan estimasi biaya sesuai skema
            diagram sistem solar PowerX.
          </p>
        </div>
      </section>

      {/* Gate */}
      {!systems ? (
        <section className="max-w-md mx-auto px-6 py-20">
          <div className="bg-neutral-50 border border-neutral-200 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-px-red text-white p-3 rounded-xl">
                <Lock className="w-5 h-5" />
              </div>
              <div>
                <h2 className="font-display text-xl font-bold text-neutral-900">
                  Halaman terkunci
                </h2>
                <p className="text-sm text-neutral-500">
                  Masukkan password untuk melihat estimasi biaya
                </p>
              </div>
            </div>
            <form onSubmit={unlock} className="space-y-4">
              <div className="relative">
                <input
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  autoFocus
                  data-testid="estimate-password"
                  className="w-full bg-white border border-neutral-300 rounded-full px-5 py-3 pr-12 outline-none focus:border-px-red focus:ring-2 focus:ring-red-200 text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700"
                  aria-label="toggle password"
                  tabIndex={-1}
                >
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {error && (
                <p className="text-sm text-px-red" data-testid="estimate-error">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                disabled={loading}
                data-testid="estimate-unlock"
                className="w-full bg-px-red hover:bg-red-700 text-white rounded-full py-6 text-sm font-semibold"
              >
                {loading ? 'Memeriksa…' : 'Lihat Estimasi Biaya'}
              </Button>
            </form>
          </div>
        </section>
      ) : (
        <section className="max-w-[1200px] mx-auto px-6 lg:px-12 py-14">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
            <p className="text-sm text-neutral-500">
              Estimasi mengacu daftar harga dealer · dapat berubah sewaktu-waktu.
            </p>
            <button
              onClick={logout}
              data-testid="estimate-logout"
              className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900 border border-neutral-300 rounded-full px-4 py-2"
            >
              <LogOut className="w-4 h-4" /> Keluar
            </button>
          </div>

          {/* System switcher */}
          {Object.keys(systems).length > 1 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {Object.entries(systems).map(([key, s]) => (
                <button
                  key={key}
                  onClick={() => setParams({ s: key })}
                  data-testid={`estimate-switch-${key}`}
                  className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors ${
                    activeKey === key
                      ? 'bg-neutral-900 text-white border-neutral-900'
                      : 'bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400'
                  }`}
                >
                  {s.switch_label || s.title}
                </button>
              ))}
            </div>
          )}

          {/* Skema */}
          <div className="bg-neutral-950 text-white rounded-3xl p-8 md:p-10 mb-10 relative overflow-hidden">
            <div
              className="absolute inset-0 opacity-25"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 85% 20%, rgba(239,68,68,0.5), transparent 50%)',
              }}
            />
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-px-red p-2.5 rounded-xl">
                  <Calculator className="w-5 h-5" />
                </div>
                <p className="text-[11px] tracking-[0.25em] text-red-400 font-semibold">
                  SKEMA SISTEM
                </p>
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-bold mb-3" data-testid="estimate-title">
                {estimate.title}
              </h2>
              <p className="text-neutral-300 text-sm md:text-base max-w-3xl leading-relaxed">
                {estimate.summary}
              </p>
            </div>
          </div>

          {/* Tabel estimasi */}
          <div className="overflow-x-auto rounded-2xl border border-neutral-200" data-testid="estimate-table">
            <table className="w-full text-sm">
              <thead className="bg-neutral-900 text-white">
                <tr>
                  {estimate.columns.map((c, ci) => (
                    <th
                      key={ci}
                      className={`px-4 py-3 font-semibold whitespace-nowrap ${
                        ci >= 4 ? 'text-right' : 'text-left'
                      } ${ci === 0 ? 'w-12' : ''}`}
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {estimate.items.map((row, ri) => (
                  <tr key={ri} className={ri % 2 ? 'bg-neutral-50' : 'bg-white'}>
                    <td className="px-4 py-3 text-neutral-500">{row[0]}</td>
                    <td className="px-4 py-3 text-neutral-900 font-medium">{row[1]}</td>
                    <td className="px-4 py-3 text-neutral-600 whitespace-nowrap">{row[2]}</td>
                    <td className="px-4 py-3 text-neutral-900 text-center font-semibold">{row[3]}</td>
                    <td className="px-4 py-3 text-neutral-700 text-right whitespace-nowrap">{row[4]}</td>
                    <td className="px-4 py-3 text-neutral-900 text-right font-semibold whitespace-nowrap">{row[5]}</td>
                    <td className="px-4 py-3 text-neutral-500 text-xs">{row[6]}</td>
                  </tr>
                ))}
                <tr className="bg-red-50 border-t-2 border-px-red">
                  <td colSpan={5} className="px-4 py-4 font-display font-bold text-neutral-900 text-base">
                    TOTAL ESTIMASI BIAYA
                  </td>
                  <td className="px-4 py-4 text-right font-display font-bold text-px-red text-xl whitespace-nowrap" data-testid="estimate-total">
                    {estimate.total}
                  </td>
                  <td className="px-4 py-4" />
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-8 p-6 bg-neutral-50 border border-neutral-200 rounded-2xl text-sm text-neutral-600 leading-relaxed">
            <strong className="text-neutral-900">Catatan:</strong> Estimasi di
            atas mengacu pada daftar harga dealer PowerX dan hanya mencakup
            perangkat pada skema diagram. Belum termasuk jasa instalasi,
            struktur mounting atap, dan pengiriman. Untuk penawaran resmi &
            sizing sesuai kebutuhan aktual, silakan hubungi tim PowerX.
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              asChild
              className="bg-px-red hover:bg-red-700 text-white rounded-full px-6 py-6 text-sm font-semibold"
            >
              <Link to="/contact" data-testid="estimate-cta">
                Minta Penawaran Resmi <ArrowUpRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
            {estimate.article && (
              <Button
                asChild
                variant="outline"
                className="rounded-full px-6 py-6 text-sm font-semibold"
              >
                <Link to={estimate.article} data-testid="estimate-back-article">
                  Lihat Skema Diagram Sistem
                </Link>
              </Button>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
